import React from 'react'
import { Join } from '../../lib/utils'

/**
 * `<Section>` — Full-width semantic `<section>` wrapper with consistent
 * horizontal padding. `relative isolate` is on by default so any absolutely
 * positioned children (background blobs, decorative stripes) stay within the
 * section's painted area.
 *
 * Server-component safe.
 */
export interface SectionProps {
  className?: string
  /** @deprecated Use `className`. Kept as an alias so existing consumer code
   *  (`<Section classes="…">`) keeps compiling during the Phase A migration.
   *  Both props merge; `className` wins on conflict. */
  classes?: string
  children: React.ReactNode
  id?: string
}

export const Section = React.forwardRef<HTMLElement, SectionProps>(
  ({ className = '', classes = '', children, id }, ref) => {
    return (
      <section
        ref={ref}
        id={id}
        className={Join(
          'relative isolate w-full px-6 sm:px-10',
          classes,
          className,
        )}
      >
        {children}
      </section>
    )
  },
)
Section.displayName = 'Section'

/**
 * `<Container>` — Centered max-width inner block. The `size` prop maps to a
 * Tailwind max-w utility; explicit `max-w-*` classes via `className` still
 * work (and override the size prop when both are passed).
 */
export type ContainerSize =
  | 'sm'
  | 'md'
  | 'lg'
  | 'xl'
  | '2xl'
  | '3xl'
  | '4xl'
  | '5xl'
  | '6xl'
  | '7xl'
  | '8xl'
  | '9xl'
  | 'full'

const sizeClass: Record<ContainerSize, string> = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-xl',
  '2xl': 'max-w-2xl',
  '3xl': 'max-w-3xl',
  '4xl': 'max-w-4xl',
  '5xl': 'max-w-5xl',
  '6xl': 'max-w-6xl',
  '7xl': 'max-w-7xl',
  '8xl': 'max-w-8xl',
  '9xl': 'max-w-9xl',
  full: 'max-w-none',
}

export interface ContainerProps {
  className?: string
  /** @deprecated Use `className`. Kept as an alias for back-compat. */
  classes?: string
  children: React.ReactNode
  id?: string
  /**
   * Max-width preset. When omitted, no `max-w-*` utility is emitted, leaving
   * the consumer free to specify the width via `className` (existing
   * behaviour). Pass `'full'` to make this explicit.
   */
  size?: ContainerSize
}

export const Container = React.forwardRef<HTMLDivElement, ContainerProps>(
  ({ className = '', classes = '', children, id, size }, ref) => {
    return (
      <div
        ref={ref}
        id={id}
        className={Join(
          'mx-auto w-full',
          size ? sizeClass[size] : '',
          classes,
          className,
        )}
      >
        {children}
      </div>
    )
  },
)
Container.displayName = 'Container'
